// Overview tab for the detail page. Composes the 4-up metrics grid, the
// delivery funnel, an activity chart, and the top-clicked-links table.
// All metric selection is channel/offer-aware via metricsFor().

import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Typo, ListTable } from '../../../../ui';
import { metricFormatters, metricsFor } from '../../utils/metricFormatters';
import { CHANNEL, FEATURE_FLAG } from '../../constants';
import { useFeatures } from '../../../settings/hooks/useSettingsState';
import * as mockBackend from '../../mockBackend';

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
`;

const MetricCard = styled.div`
  flex: 1 1 160px;
  background: ${({ theme }) => theme.colors.NEU_0};
  border: 1px solid ${({ theme }) => theme.colors.NEU_200};
  border-radius: 10px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MetricLabel = styled.div`
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.NEU_600};
`;

const MetricValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.NEU_PRIMARY};
`;

const MetricHint = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.NEU_600};
`;

const Section = styled.section`
  background: ${({ theme }) => theme.colors.NEU_0};
  border: 1px solid ${({ theme }) => theme.colors.NEU_200};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
`;

const SectionTitle = styled.div`
  font-weight: 600;
  margin-bottom: 14px;
`;

const FunnelRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const FunnelStep = styled.div`
  flex: 1 1 120px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  background: ${({ theme }) => theme.colors.NEU_50};
  border-radius: 10px;
`;

const FunnelLabel = styled.div`
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.NEU_600};
`;

const FunnelValue = styled.div`
  font-size: 18px;
  font-weight: 700;
`;

const FunnelBar = styled.div`
  height: 4px;
  background: ${({ theme }) => theme.colors.NEU_100};
  border-radius: 2px;
  overflow: hidden;
`;

const FunnelFill = styled.div`
  height: 100%;
  width: ${({ $pct }) => `${Math.max(2, $pct)}%`};
  background: ${({ theme, $variant }) => {
    if ($variant === 'sent') return theme.colors.PRIMARY;
    if ($variant === 'delivered') return theme.colors.PRIMARY_LIGHT;
    if ($variant === 'opened') return theme.colors.MARKER;
    if ($variant === 'clicked') return theme.colors.SECONDARY;
    if ($variant === 'converted') return theme.colors.SUCCESS_GREEN;
    return theme.colors.PRIMARY;
  }};
`;

const FunnelHint = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.NEU_600};
`;

const ChartFrame = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
`;

const NotLive = styled.span`
  display: inline-block;
  padding: 2px 6px;
  background: ${({ theme }) => theme.colors.NEU_100};
  color: ${({ theme }) => theme.colors.NEU_600};
  border-radius: 4px;
  font-size: 11px;
`;

const readValue = (metrics, source) => {
  if (!source) return undefined;
  const [, key] = source.split('.');
  return metrics?.[key];
};

const formatWithDescriptor = (descriptor, metrics) => {
  const fn = metricFormatters[descriptor.format] ?? metricFormatters.number;
  const raw = descriptor.compute ? descriptor.compute(metrics) : readValue(metrics, descriptor.source);
  return fn(raw);
};

// Lightweight inline line chart — two lines (opens, clicks), no axis labels
// beyond the time markers on the bottom. Keeps the bundle free of recharts.
const MiniLineChart = ({ series, theme }) => {
  const width = 640;
  const height = 200;
  const padding = 24;
  const maxOpens = Math.max(...series.map((p) => p.opens), 1);
  const maxClicks = Math.max(...series.map((p) => p.clicks), 1);
  const maxY = Math.max(maxOpens, maxClicks);
  const maxT = series[series.length - 1]?.tMinutes || 1;

  const scaleX = (t) => padding + ((t / maxT) * (width - padding * 2));
  const scaleY = (v) => height - padding - ((v / maxY) * (height - padding * 2));

  const toPath = (key) => series.map((p, i) => {
    const cmd = i === 0 ? 'M' : 'L';
    return `${cmd} ${scaleX(p.tMinutes).toFixed(1)} ${scaleY(p[key]).toFixed(1)}`;
  }).join(' ');

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width='100%' height='200'>
      <rect x='0' y='0' width={width} height={height} fill='transparent' />
      <path d={toPath('opens')} stroke={theme.colors.PRIMARY} strokeWidth='2' fill='none' />
      <path d={toPath('clicks')} stroke={theme.colors.SECONDARY} strokeWidth='2' fill='none' />
      <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke={theme.colors.NEU_100} />
    </svg>
  );
};

const OverviewTab = ({ campaign, theme }) => {
  const { t } = useTranslation();
  const features = useFeatures();
  const upsellLive = !!features?.[FEATURE_FLAG.NETWORK_UPSELL_LIVE];
  const isUpsellOnly = campaign?.channels?.length === 1 && campaign.channels.includes(CHANNEL.NETWORK_UPSELL);

  const descriptors = useMemo(() => metricsFor({ channels: campaign?.channels, offer: campaign?.offer }), [campaign]);
  const metrics = campaign?.metrics ?? {};
  const series = useMemo(() => mockBackend.getActivitySeries(), []);
  const topLinks = useMemo(() => mockBackend.getTopLinks(), []);

  const funnel = useMemo(() => {
    const sent = metrics.recipients ?? 0;
    return [
      { key: 'sent', label: 'Sent', value: metrics.recipients ?? 0, pct: 100, hint: '100%' },
      { key: 'delivered', label: 'Delivered', value: metrics.delivered ?? 0, pct: sent ? (metrics.delivered / sent) * 100 : 0, hint: metrics.bounces ? `${((metrics.delivered / sent) * 100).toFixed(1)}% · ${metrics.bounces} bounces` : '' },
      { key: 'opened', label: 'Opened', value: metrics.opened ?? 0, pct: sent ? (metrics.opened / sent) * 100 : 0, hint: sent ? `${((metrics.opened / sent) * 100).toFixed(1)}%` : '' },
      { key: 'clicked', label: 'Clicked', value: metrics.clicked ?? 0, pct: sent ? (metrics.clicked / sent) * 100 : 0, hint: sent ? `${((metrics.clicked / sent) * 100).toFixed(1)}%` : '' },
      { key: 'converted', label: 'Converted', value: metrics.converted ?? 0, pct: sent ? (metrics.converted / sent) * 100 : 0, hint: sent ? `${((metrics.converted / sent) * 100).toFixed(1)}% · $${(metrics.revenue ?? 0).toLocaleString()}` : '' },
    ];
  }, [metrics]);

  return (
    <div>
      <Grid>
        {descriptors.map((d) => {
          const notLive = isUpsellOnly && !upsellLive;
          return (
            <MetricCard key={d.key}>
              <MetricLabel>{t(`campaigns.metric.${d.key}`, d.label)}</MetricLabel>
              {notLive ? (
                <>
                  <MetricValue>—</MetricValue>
                  <MetricHint><NotLive>{t('campaigns.metric.notLive', 'Not live yet')}</NotLive></MetricHint>
                </>
              ) : (
                <MetricValue>{formatWithDescriptor(d, metrics)}</MetricValue>
              )}
            </MetricCard>
          );
        })}
      </Grid>

      <Section>
        <SectionTitle>{t('campaigns.overview.deliveryFunnel', 'Delivery funnel')}</SectionTitle>
        <FunnelRow>
          {funnel.map((step) => (
            <FunnelStep key={step.key}>
              <FunnelLabel>{t(`campaigns.funnel.${step.key}`, step.label)}</FunnelLabel>
              <FunnelValue>{step.value.toLocaleString()}</FunnelValue>
              <FunnelBar><FunnelFill $pct={step.pct} $variant={step.key} /></FunnelBar>
              <FunnelHint>{step.hint}</FunnelHint>
            </FunnelStep>
          ))}
        </FunnelRow>
      </Section>

      <Section>
        <SectionTitle>{t('campaigns.overview.activityOverTime', 'Activity over time')}</SectionTitle>
        <ChartFrame>
          <MiniLineChart series={series} theme={theme} />
        </ChartFrame>
        <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 12, color: theme.colors.NEU_600 }}>
          <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: theme.colors.PRIMARY, marginRight: 6 }} />{t('campaigns.funnel.opened', 'Opens')}</span>
          <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: theme.colors.SECONDARY, marginRight: 6 }} />{t('campaigns.funnel.clicked', 'Clicks')}</span>
        </div>
      </Section>

      <Section>
        <SectionTitle>{t('campaigns.overview.topLinks', 'Top clicked links')}</SectionTitle>
        {topLinks.length === 0 ? (
          <Typo.SmallText color='textSecondary' style={{ padding: 16, textAlign: 'center' }}>
            {t('campaigns.overview.noLinks', 'No links have been clicked yet.')}
          </Typo.SmallText>
        ) : (
          <ListTable
            tableName='top-links'
            data={topLinks.map((l, i) => ({ id: `${l.url}-${i}`, ...l }))}
            tableInfo={[
              { label: 'URL', data: (item) => item.url },
              { label: t('campaigns.metric.clicked', 'Clicks'), data: (item) => item.clicks },
              { label: t('campaigns.metric.unique', 'Unique'), data: (item) => item.unique },
              { label: '%', data: (item) => `${item.percent.toFixed(1)}%` },
            ]}
            defaultRowPerPage={10}
          />
        )}
      </Section>

      <Typo.SmallText color='textSecondary' style={{ fontSize: 11 }}>
        {t('campaigns.overview.mppNote', 'Note: some email clients pre-fetch images. Treat open rate as a trend indicator.')}
      </Typo.SmallText>
    </div>
  );
};

export default OverviewTab;
